import { useState } from 'react'

import { onFormChangeable, onFormSubmitable } from 'types'

export const useForm = <T>(
  onSubmitCallback: (inputs: T) => void = () => undefined,
  validateFormDataCallback: (
    inputs: T,
  ) => Partial<{ [key in keyof T]: string }> = formInputs => ({}),
): [
  T,
  (event: onFormChangeable) => void,
  (event: onFormSubmitable) => void,
  boolean,
  (newFormValue: Partial<T>) => void,
  Partial<{ [key in keyof T]: string }>,
] => {
  const [inputs, setInputs] = useState({} as T)
  const [validated, setValidated] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})

  const includeFormManagedValue = (newFormValue: Partial<T>) => {
    setInputs(oldInputs => ({
      ...oldInputs,
      ...newFormValue,
    }))
  }

  const handleInputChange = (event: onFormChangeable) => {
    // Warning: This synthetic event is reused for performance reasons.
    // If you're seeing this, you're accessing the property `target` on a released/nullified synthetic event.
    // If you must keep the original synthetic event around, use event.persist().
    // See https://fb.me/react-event-pooling for more information.
    event.persist()
    const { name, value, files } = event.target
    setInputs(oldInputs => ({
      ...oldInputs,
      [name]: files ? files[0] : value,
    }))
  }

  const handleSubmit = (event: onFormSubmitable) => {
    const form = event.currentTarget
    event.preventDefault()
    const errors = validateFormDataCallback
      ? validateFormDataCallback(inputs)
      : {}
    const numErrors = Object.keys(errors).length

    if (form.checkValidity() && !numErrors) {
      onSubmitCallback(inputs)
    } else {
      event.stopPropagation()
      if (numErrors) {
        setValidationErrors(errors)
      }
    }
    setValidated(true)
  }

  return [
    inputs,
    handleInputChange,
    handleSubmit,
    validated,
    includeFormManagedValue,
    validationErrors,
  ]
}
